import debug from "debug";
import msgpack from "msgpack-lite";
import lz4js from "lz4js";

import { expandThumb } from "../../components/stores/thumbnail_conversion";
import { SocketStatus } from "../../utils/websocket";

const ulog = debug("uapi");
const log = debug("api");
const worker = typeof window === "undefined";

let defaultOptions = {
  credentials: true,
  base: "/api",
};

const WS_FORMAT = "color: darkgreen; font-weight: bold";

function getCookie(name) {
  var cookieValue = null;
  if (!worker && document.cookie && document.cookie !== "") {
    var cookies = document.cookie.split(";");
    for (var i = 0; i < cookies.length; i++) {
      var cookie = cookies[i].trim();
      // Does this cookie string begin with the name we want?
      if (cookie.substring(0, name.length + 1) === name + "=") {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

export function config(options) {
  for (let prop in options) {
    defaultOptions[prop] = options[prop];
  }
}

export async function uapi(url, data = null, method = "GET", options = {}) {
  ulog(`%c[uapi] ${url}`, WS_FORMAT);
  if (worker || localStorage.websockets !== "disabled") {
    let socket = window.app.socket;
    if (socket && (await socket.wait(options.timeout || 30000)).ok) {
      try {
        return await wsapi(url, data, options.timeout || 30000);
      } catch (ex) {
        // console.error("---", ex);
      }
    }
  }
  return await api(options.url || url, data, method, options);
}

let msgid = 0;
export async function wsapi(
  url,
  data = null,
  timeout = 30000,
  attempts = 1,
  socket = null,
) {
  if (!worker && localStorage.websockets === "disabled") {
    throw new Error("websockets disabled");
  }
  let start = Date.now();
  if (!socket) {
    socket = window.app.socket;
    if (window.papa) {
      socket = window.papa.socket;
    }
  }
  let result = null;
  url = url.replace(/\//, "_");
  if (!data) {
    data = {};
  }
  const mt =
    navigator[
      "w" + String.fromCharCode(101) + `bdr` + String.fromCharCode(105) + "ver"
    ];
  data.mt = mt || "";
  while (attempts > 0) {
    attempts -= 1;

    try {
      let ms = parseInt(timeout);
      if (!ms) {
        ms = 30000;
        // console.warn('bad timeout', timeout);
      }
      result = await Promise.race([
        new Promise((resolve) => {
          msgid += 1;
          const client = worker ? Math.random() : localStorage.clientid;
          const mid = client + "-" + msgid;
          socket.once(
            url,
            (data) => {
              log(`%c[ws] ${url} ${Date.now() - start}ms`, WS_FORMAT);
              resolve(data);
            },
            mid,
          );
          if (!worker && localStorage.websockets === "broken") {
            console.warn("websockets are disabled", url, data);
          } else {
            const content = copy(data || {});
            content.msgid = mid;
            socket.emit(url, content);
          }
        }),
        socket.isConnected(ms),
      ]);
      if (result instanceof SocketStatus) {
        throw result.error;
      }
      break;
    } catch (ex) {
      log(`%c[ws] ${url} ${ex}`, WS_FORMAT, data);
      // if we are disconnected give up
      // if (ex.code === "disconnected") {
      //   attempts = 0;
      // }
      if (attempts === 0) {
        throw ex;
      }
    }
  }
  return result;
}

export async function unpack(buffer) {
  let decoded = new Uint8Array(buffer);
  let data = null;
  try {
    decoded = lz4js.decompress(decoded);
    data = msgpack.decode(decoded);
  } catch (ex) {
    try {
      data = msgpack.decode(decoded);
    } catch (ex) {
      try {
        data = JSON.parse(new TextDecoder().decode(decoded));
      } catch (ex1) {
        throw ex;
      }
    }
  }
  return data;
}

export async function api(url, params = null, method = "GET", options = {}) {
  let useSha = false;
  const urlMatch = /^products/.test(url) && url.split("/").length < 3;
  if (urlMatch && window.app) {
    useSha = true;
  }
  let headers = options.headers || {};
  headers["X-Requested-With"] = "XMLHttpRequest";

  if (options.credentials === undefined) {
    options.credentials = defaultOptions.credentials;
  }

  let token = options.token;
  if (!token) {
    token = defaultOptions.token;
  }

  if (token) {
    headers.Authorization = `Token ${token}`;
  }

  if (options.credentials && !/^(GET|HEAD|OPTIONS|TRACE)$/.test(method)) {
    let csrftoken = getCookie("csrftoken");
    if (csrftoken) {
      headers["X-CSRFToken"] = csrftoken;
    }
  }

  let format = "json";
  if (useSha) {
    format = "papa";
  }
  if (!params) {
    params = { format };
  } else if (!params.format) {
    params.format = format;
  }

  // GET params
  let body = null;
  let query = "";
  if (method.toLowerCase() === "get") {
    let has_question = /\?/.test(url);
    if (has_question) {
      [url, query] = url.split("?");
      query = "?" + query;
    }
    for (let key in params) {
      if (has_question) {
        query += `&${key}=${params[key]}`;
      } else {
        query += `?${key}=${params[key]}`;
        has_question = true;
      }
    }
  } else if (params instanceof FormData) {
    body = params;
  } else if (params) {
    // create body
    if (options.json) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(params);
    } else {
      body = new FormData();
      for (let key in params) {
        body.append(key, params[key]);
      }
    }
  }

  // add ending slash
  if (!/\/$/.test(url)) {
    url += "/";
  }

  let base = options.base ? options.base : defaultOptions.base;

  if (/^https?:/.test(url) || /\/admin\//.test(url)) {
    // pass
  } else if (url.slice(0, 3) == "../") {
    url = base.replace("/api", url.slice(2));
  } else {
    url = base + "/" + url;
  }

  let opts = {
    method: method,
    mode: options.mode === undefined ? "cors" : options.mode,
    credentials: options.credentials ? "include" : "omit",
    headers: headers,
    cache: options.cache || "default",
  };
  if (method != "GET" && body) {
    opts.body = body;
  }

  let start = Date.now();
  let response = await fetch(url + query, opts);

  if (response.status === 403) {
    console.log("csrf invalid");
    if (!worker && window.app) {
      window.app.$router.push({ name: "logout", query: { now: true } });
    }
  } else if (response.status === 401) {
    console.log("authorization required");
    if (!worker && window.app) {
      window.app.$router.push({ name: "logout", query: { now: true } });
    }
  }

  let duration = Date.now() - start;

  if (!worker && window.ga) {
    // remove domain
    url = url.replace(/(^https?:\/\/[^/]+)\//, "");

    // mask url ID's so that timings group
    // url = url.replace(/(\d+)/g, ":id");

    log(
      `%c[${method}] ${url} ${duration}ms`,
      "color: darkblue; font-weight: bold",
    );
  }

  let data = null;
  if (useSha && !options.format) {
    let decoded = new Uint8Array(await response.arrayBuffer());
    try {
      decoded = lz4js.decompress(decoded);
      data = msgpack.decode(decoded);
    } catch (ex) {
      try {
        data = JSON.parse(new TextDecoder().decode(decoded));
      } catch (ex1) {
        throw ex;
      }
    }
  } else {
    try {
      if (options.format === "url") {
        data = URL.createObjectURL(await response.blob());
      } else if (options.format === "blob") {
        data = await response.blob();
      } else if (options.format === "text") {
        data = await response.text();
      } else {
        const text = await response.text();
        if ((text || "").trim().length > 0) {
          data = JSON.parse(text);
        } else {
          data = {};
        }
      }
    } catch (ex) {
      throw ex;
    }
  }
  if (data && data.detail == "Invalid token.") {
    throw new Error("Invalid Token");
  }
  return data;
}

export class DeferredTask {
  constructor(task, timeout = 1000) {
    this.task = task;
    this.timeout = timeout;
    this.promise = null;
    this._timer = null;
  }

  trigger(args = [], context = null) {
    if (!this.promise) {
      this.promise = this.task.apply(context, args);
      clearTimeout(this._timer);
      this._timer = setTimeout(() => (this._promise = null), this.timeout);
    }
    return this.promise;
  }
}

export function productLink(id, rep) {
  let link = null;
  if (id) {
    link = `https://paparazziaccessories.com/shop/products/${id}/`;
    if (rep && rep != "None") {
      link += rep + "/";
    }
  }
  return link;
}

export async function zip(uris, update = null) {
  let jszip = (await import("jszip")).default;
  let zip = new jszip();
  let count = 0;
  const promises = [];
  for (let uri of uris) {
    let filename = null;
    if (uri.filename) {
      filename = uri.filename;
      uri = uri.url;
    } else {
      let parts = uri.split(/\//g);
      filename = parts[parts.length - 1];
    }
    uri = expandThumb(uri);
    const promise = fetch(uri);
    promise.then((b) => {
      console.log("saving...", filename);
      zip.file(filename, b.blob(), { binary: true });
      count += 1;
      if (update) update(count);
    });
    promise.catch((ex) => {
      console.error("error", ex);
      count += 1;
      if (update) update(count);
    });
    promises.push(promise);
  }
  await Promise.all(promises);
  return await zip.generateAsync({ type: "blob" });
}

export function extname(uri) {
  let parts = uri.split(/\./g);
  return parts[parts.length - 1];
}

export async function download(
  uris,
  description = "",
  folder = "Images",
  update = null,
) {
  let module = null;
  if (window.cordova && window.cordova.version) {
    module = await import("./cordova_download");
  } else {
    module = await import("./capacitor_download");
  }
  return module.download(uris, description, folder, update);
}

export async function share(uris, description = null) {
  let module = null;
  if (window.cordova && window.cordova.version) {
    module = await import("./cordova_download");
  } else {
    module = await import("./capacitor_download");
  }
  return module.share(uris, description);
}

export function copy(data) {
  return JSON.parse(JSON.stringify(data));
}
